import _slicedToArray from "/opt/graphistry/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import _defineProperty from "/opt/graphistry/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/defineProperty";
import _objectSpread from "/opt/graphistry/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/objectSpread2";
import React, { useState, useEffect } from 'react';
import Card from 'react-bootstrap/Card';
import Autocomplete from '@mui/material/Autocomplete';
import Container from 'react-bootstrap/Container';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import ListItem from '@mui/material/ListItem';
import Slider from '@mui/material/Slider';
import TextField from '@mui/material/TextField';
import RcSwitch from 'rc-switch';
import { styled } from '@mui/material/styles';
import styles from '../styles/file-uploader.less';
import { bindingsReady } from './bindings';
import { fetchApiSchema, emptyApiSchema } from './apiSchema';
import { hasNodeAttributes, inferNodeAttributes } from './inference';
import logger from '../utils/logger';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
var log = logger.createLogger('Algorithms');
var Root = styled("div")(function (_ref) {
  var theme = _ref.theme;
  return {
    display: 'flex'
  };
});
var FormControlStyle = styled("div")(function (_ref2) {
  var theme = _ref2.theme;
  return {
    margin: theme.spacing(3)
  };
});
function wrapper(body) {
  return /*#__PURE__*/_jsxs("div", {
    className: "".concat(styles['sharer']),
    children: [/*#__PURE__*/_jsxs("h5", {
      children: [/*#__PURE__*/_jsx("i", {
        children: "Optional:"
      }), " Add graph algorithm insights"]
    }), body]
  });
}
function makeAlgs(_ref3) {
  var prefix = _ref3.prefix,
    label = _ref3.label,
    library = _ref3.library;
  function symbolToAlgorithm(s) {
    return s.slice(2);
  }
  function algorithmToSymbol(a) {
    return "".concat(prefix).concat(a);
  }
  function symbolIsLibrary(s) {
    return s.startsWith(prefix);
  }
  return function (_ref4) {
    var compute = _ref4.compute,
      options = _ref4.options,
      setVisualizationBindings = _ref4.setVisualizationBindings;
    var activeVals = Object.keys(compute || {}).filter(symbolIsLibrary).map(symbolToAlgorithm);
    log.debug({
      activeVals: activeVals,
      compute: compute
    }, "activeAlgs: ".concat(prefix));
    return /*#__PURE__*/_jsx(Autocomplete, {
      multiple: true,
      options: options,
      value: activeVals,
      filterSelectedOptions: true,
      renderInput: function renderInput(params) {
        return /*#__PURE__*/_jsx(TextField, _objectSpread(_objectSpread({}, params), {}, {
          variant: "outlined",
          label: label,
          placeholder: ""
        }));
      },
      onChange: function onChange(event, newValues) {
        //log.debug({event, newValues}, 'alg change');

        var computeNew = _objectSpread({}, compute || {});
        Object.keys(computeNew).forEach(function (symbol) {
          if (symbolIsLibrary(symbol)) {
            delete computeNew[symbol];
          }
        });
        newValues.forEach(function (alg) {
          computeNew[algorithmToSymbol(alg)] = {
            library: library,
            alg: alg
          };
        });
        setVisualizationBindings(function (bindings) {
          return _objectSpread(_objectSpread({}, bindings), {}, {
            remote: _objectSpread(_objectSpread({}, bindings.remote), {}, {
              compute: computeNew
            })
          });
        });
      }
    });
  };
}
var CPUAlgs = makeAlgs({
  prefix: 'i_',
  label: 'igraph',
  library: 'igraph'
});
var GPUAlgs = makeAlgs({
  prefix: 'c_',
  label: 'cugraph (GPU-accelerated)',
  library: 'cugraph'
});
function UMAPOptions(_ref5) {
  var visualizationBindings = _ref5.visualizationBindings,
    umapPosition = _ref5.umapPosition,
    setUMAPPosition = _ref5.setUMAPPosition,
    umapColumns = _ref5.umapColumns,
    setUMAPColumns = _ref5.setUMAPColumns,
    umapTargets = _ref5.umapTargets,
    setUMAPTargets = _ref5.setUMAPTargets,
    umapEdges = _ref5.umapEdges,
    setUMAPEdges = _ref5.setUMAPEdges,
    umapDropRegularEdges = _ref5.umapDropRegularEdges,
    setUMAPDropRegularEdges = _ref5.setUMAPDropRegularEdges,
    umapFeatureEngineering = _ref5.umapFeatureEngineering,
    setUMAPFeatureEngineering = _ref5.setUMAPFeatureEngineering,
    umapScale = _ref5.umapScale,
    setUMAPScale = _ref5.setUMAPScale,
    umapMinDist = _ref5.umapMinDist,
    setUMAPMinDist = _ref5.setUMAPMinDist,
    umapLocalConnectivity = _ref5.umapLocalConnectivity,
    setUMAPLocalConnectivity = _ref5.setUMAPLocalConnectivity,
    umapNegativeSamplingRate = _ref5.umapNegativeSamplingRate,
    setUMAPNegativeSamplingRate = _ref5.setUMAPNegativeSamplingRate,
    umapSuffix = _ref5.umapSuffix,
    setUMAPSuffix = _ref5.setUMAPSuffix,
    umapMetric = _ref5.umapMetric,
    setUMAPMetric = _ref5.setUMAPMetric,
    layout = _ref5.layout,
    setLayout = _ref5.setLayout;
  return /*#__PURE__*/_jsxs(Root, {
    children: [/*#__PURE__*/_jsx(FormControlStyle, {
      children: /*#__PURE__*/_jsxs(FormControl, {
        component: "fieldset",
        children: [/*#__PURE__*/_jsx(FormLabel, {
          component: "legend",
          children: "Core UMAP Settings"
        }), /*#__PURE__*/_jsxs(FormGroup, {
          children: [/*#__PURE__*/_jsx(FormControlLabel, {
            control: /*#__PURE__*/_jsx(RcSwitch, {
              onChange: function onChange(value) {
                setUMAPPosition(value);
                if (value && layout !== 'umap') {
                  setLayout('umap');
                } else if (!value && layout === 'umap') {
                  setLayout('fa2');
                }
              },
              checked: umapPosition,
              checkedChildren: 'on',
              unCheckedChildren: 'on'
            }),
            label: "Position nodes based on UMAP"
          }), /*#__PURE__*/_jsx(FormControlLabel, {
            control: /*#__PURE__*/_jsx(RcSwitch, {
              onChange: function onChange(value) {
                setUMAPEdges(value);
              },
              checked: umapEdges,
              checkedChildren: 'on',
              unCheckedChildren: 'on'
            }),
            label: "Create similarity edges"
          }), /*#__PURE__*/_jsx(FormControlLabel, {
            control: /*#__PURE__*/_jsx(RcSwitch, {
              onChange: function onChange(value) {
                setUMAPDropRegularEdges(value);
              },
              checked: umapDropRegularEdges,
              checkedChildren: 'on',
              unCheckedChildren: 'on'
            }),
            label: "Remove non-UMAP-generated edges"
          }), /*#__PURE__*/_jsx(_Fragment, {
            children: function () {
              var attributes = inferNodeAttributes(visualizationBindings);
              var hasNodes = hasNodeAttributes(visualizationBindings);
              return /*#__PURE__*/_jsxs(_Fragment, {
                children: [/*#__PURE__*/_jsx(FormControlLabel, {
                  control: /*#__PURE__*/_jsx(Autocomplete, {
                    multiple: true,
                    options: attributes.filter(function (a) {
                      return !umapTargets.includes(a);
                    }),
                    value: umapColumns,
                    filterSelectedOptions: true,
                    disabled: !hasNodes,
                    renderInput: function renderInput(params) {
                      return /*#__PURE__*/_jsx(TextField, _objectSpread(_objectSpread({}, params), {}, {
                        variant: "outlined",
                        label: 'X',
                        placeholder: ""
                      }));
                    },
                    onChange: function onChange(event, newValue) {
                      log.debug({
                        event: event,
                        newValue: newValue
                      }, 'X');
                      setUMAPColumns(newValue);
                    }
                  }),
                  label: "Node attributes to cluster on (empty for all)"
                }), /*#__PURE__*/_jsx(FormControlLabel, {
                  control: /*#__PURE__*/_jsx(Autocomplete, {
                    multiple: true,
                    options: attributes.filter(function (a) {
                      return !umapColumns.includes(a);
                    }),
                    value: umapTargets,
                    filterSelectedOptions: true,
                    disabled: !hasNodes,
                    renderInput: function renderInput(params) {
                      return /*#__PURE__*/_jsx(TextField, _objectSpread(_objectSpread({}, params), {}, {
                        variant: "outlined",
                        label: 'y',
                        placeholder: ""
                      }));
                    },
                    onChange: function onChange(event, newValue) {
                      log.debug({
                        event: event,
                        newValue: newValue
                      }, 'y');
                      setUMAPTargets(newValue);
                    }
                  }),
                  label: "Node attributes with expected cluster labels (empty for unsupervised learning)"
                })]
              });
            }()
          })]
        })]
      })
    }), /*#__PURE__*/_jsx(FormControlStyle, {
      children: /*#__PURE__*/_jsxs(FormControl, {
        component: "fieldset",
        children: [/*#__PURE__*/_jsx(FormLabel, {
          component: "legend",
          children: "Advanced"
        }), /*#__PURE__*/_jsxs(FormGroup, {
          children: [/*#__PURE__*/_jsx(FormControlLabel, {
            control: /*#__PURE__*/_jsx(Autocomplete, {
              options: ["none", "auto", "pandas", "dirty_cat", "torch"],
              value: umapFeatureEngineering,
              filterSelectedOptions: true,
              renderInput: function renderInput(params) {
                return /*#__PURE__*/_jsx(TextField, _objectSpread(_objectSpread({}, params), {}, {
                  variant: "outlined",
                  label: 'eature extractor',
                  placeholder: ""
                }));
              },
              onChange: function onChange(event, newValue) {
                log.debug({
                  event: event,
                  newValue: newValue
                }, 'feat');
                setUMAPFeatureEngineering(newValue);
              }
            }),
            label: "Automatic feature extraction"
          }), /*#__PURE__*/_jsx(FormControlLabel, {
            control: /*#__PURE__*/_jsx(Slider, {
              size: "small",
              min: 0,
              max: 2,
              value: umapScale,
              onChange: function onChange(event, newValue) {
                setUMAPScale(newValue);
              },
              "aria-label": "Small",
              step: 0.1,
              valueLabelDisplay: "auto"
            }),
            label: "Scale"
          }), /*#__PURE__*/_jsx(FormControlLabel, {
            control: /*#__PURE__*/_jsx(Slider, {
              size: "small",
              min: 0,
              max: 1,
              value: umapMinDist,
              onChange: function onChange(event, newValue) {
                setUMAPMinDist(newValue);
              },
              "aria-label": "Small",
              step: 0.1,
              valueLabelDisplay: "auto"
            }),
            label: "Minimum distance"
          }), /*#__PURE__*/_jsx(FormControlLabel, {
            control: /*#__PURE__*/_jsx(Slider, {
              size: "small",
              min: 1,
              max: 20,
              value: umapLocalConnectivity,
              onChange: function onChange(event, newValue) {
                setUMAPLocalConnectivity(newValue);
              },
              "aria-label": "Small",
              step: 1,
              valueLabelDisplay: "auto"
            }),
            label: "Local connectivity"
          }), /*#__PURE__*/_jsx(FormControlLabel, {
            control: /*#__PURE__*/_jsx(Slider, {
              size: "small",
              min: 1,
              max: 100,
              value: umapNegativeSamplingRate,
              onChange: function onChange(event, newValue) {
                setUMAPNegativeSamplingRate(newValue);
              },
              "aria-label": "Small",
              step: 1,
              valueLabelDisplay: "auto"
            }),
            label: "Negative sampling rate"
          }), /*#__PURE__*/_jsx(FormControlLabel, {
            control: /*#__PURE__*/_jsx(Autocomplete, {
              options: [
              //Minkowski style metrics
              "euclidean", "manhattan", "chebyshev", "minkowski",
              //Miscellaneous spatial metrics
              "canberra", "braycurtis", "haversine",
              //Normalized spatial metrics
              "mahalanobis", "wminkowski", "seuclidean",
              //Angular and correlation metrics
              "cosine", "correlation",
              //Metrics for binary data
              "hamming", "jaccard", "dice", "russellrao", "kulsinski", "rogerstanimoto", "sokalmichener", "sokalsneath", "yule"],
              value: umapMetric,
              filterSelectedOptions: true,
              renderInput: function renderInput(params) {
                return /*#__PURE__*/_jsx(TextField, _objectSpread(_objectSpread({}, params), {}, {
                  variant: "outlined",
                  label: 'feature extractor',
                  placeholder: ""
                }));
              },
              onChange: function onChange(event, newValue) {
                log.debug({
                  event: event,
                  newValue: newValue
                }, 'metric');
                setUMAPMetric(newValue);
              }
            }),
            label: "Distance function"
          }), /*#__PURE__*/_jsx(FormControlLabel, {
            control: /*#__PURE__*/_jsx(TextField, {
              id: "umapSuffix",
              type: "text",
              label: "suffix",
              name: "suffix",
              defaultValue: umapSuffix,
              multiline: false,
              rows: 1,
              onChange: function onChange(event) {
                setUMAPSuffix(event.target.value);
              }
            }),
            label: "Suffix"
          })]
        })]
      })
    })]
  });
}
function isUmap(umapEnabled, visualizationBindings) {
  if (!umapEnabled || !(visualizationBindings.remote && visualizationBindings.remote.layout && visualizationBindings.remote.layout.layout === 'umap')) {
    return false;
  }
  return true;
}
function effectUmapArray(umapEnabled, visualizationBindings, setVisualizationBindings, arr, prop) {
  if (!isUmap(umapEnabled, visualizationBindings)) {
    return;
  }
  if (arr.length == (visualizationBindings.remote.layout[prop] || []).length) {
    if (arr.join(',') === (visualizationBindings.remote.layout[prop] || []).join(',')) {
      return;
    }
  }
  var layout = _objectSpread({}, (visualizationBindings.remote || {}).layout || {});
  if (arr.length) {
    layout[prop] = arr;
  } else {
    delete layout[prop];
  }
  log.debug({
    arr: arr,
    prop: prop
  }, 'effectUmapArray');
  setVisualizationBindings(_objectSpread(_objectSpread({}, visualizationBindings), {}, {
    remote: _objectSpread(_objectSpread({}, visualizationBindings.remote || {}), {}, {
      layout: layout
    })
  }));
}
function effectUmapAtomic(umapEnabled, visualizationBindings, setVisualizationBindings, val, prop) {
  if (!isUmap(umapEnabled, visualizationBindings)) {
    log.debug({
      umapEnabled: umapEnabled,
      visualizationBindings: visualizationBindings
    }, 'skip umap effect; disabled umap');
    return;
  }
  if (val === (visualizationBindings.remote.layout.params || {})[prop]) {
    log.debug({
      val: val,
      prop: prop,
      visualizationBindings: visualizationBindings
    }, 'skip umap effect; no change');
    return;
  }
  var layout = _objectSpread(_objectSpread({}, (visualizationBindings.remote || {}).layout || {}), {}, {
    params: _objectSpread(_objectSpread({}, (visualizationBindings.remote.layout || {} || {}).params), {}, _defineProperty({}, prop, val))
  });
  log.debug({
    layout: layout,
    val: val,
    prop: prop
  }, 'effectUmapAtomic changing remote layout bindings');
  setVisualizationBindings(_objectSpread(_objectSpread({}, visualizationBindings), {}, {
    remote: _objectSpread(_objectSpread({}, visualizationBindings.remote || {}), {}, {
      layout: layout
    })
  }));
}
export function Algorithms(_ref6) {
  var visible = _ref6.visible,
    user = _ref6.user,
    files = _ref6.files,
    setVisualizationBindings = _ref6.setVisualizationBindings,
    visualizationBindings = _ref6.visualizationBindings,
    visualization = _ref6.visualization,
    layout = _ref6.layout,
    setLayout = _ref6.setLayout;
  var _useState = useState(emptyApiSchema()),
    _useState2 = _slicedToArray(_useState, 2),
    schema = _useState2[0],
    setSchema = _useState2[1];
  useEffect(function () {
    log.debug('effect set scheme init');
    fetchApiSchema().then(function (v) {
      log.debug({
        v: v
      }, 'setting schema');
      if (v && v.compute) {
        setSchema(v);
      } else {
        log.warn({
          v: v
        }, 'Invalid schema response, using empty schema');
      }
    }).catch(function (e) {
      log.error({
        e: e
      }, 'Failed to fetch API schema, using empty schema');
    });
  }, []);
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    umapEnabled = _useState4[0],
    setUMAPEnabled = _useState4[1];
  var _useState5 = useState(true),
    _useState6 = _slicedToArray(_useState5, 2),
    umapPosition = _useState6[0],
    setUMAPPosition = _useState6[1];
  var _useState7 = useState([]),
    _useState8 = _slicedToArray(_useState7, 2),
    umapColumns = _useState8[0],
    setUMAPColumns = _useState8[1];
  var _useState9 = useState([]),
    _useState10 = _slicedToArray(_useState9, 2),
    umapTargets = _useState10[0],
    setUMAPTargets = _useState10[1];
  var _useState11 = useState(true),
    _useState12 = _slicedToArray(_useState11, 2),
    umapEdges = _useState12[0],
    setUMAPEdges = _useState12[1];
  var _useState13 = useState(true),
    _useState14 = _slicedToArray(_useState13, 2),
    umapDropRegularEdges = _useState14[0],
    setUMAPDropRegularEdges = _useState14[1];
  var _useState15 = useState("dirty_cat"),
    _useState16 = _slicedToArray(_useState15, 2),
    umapFeatureEngineering = _useState16[0],
    setUMAPFeatureEngineering = _useState16[1];
  var _useState17 = useState(0.5),
    _useState18 = _slicedToArray(_useState17, 2),
    umapScale = _useState18[0],
    setUMAPScale = _useState18[1];
  var _useState19 = useState(0.1),
    _useState20 = _slicedToArray(_useState19, 2),
    umapMinDist = _useState20[0],
    setUMAPMinDist = _useState20[1];
  var _useState21 = useState("euclidean"),
    _useState22 = _slicedToArray(_useState21, 2),
    umapMetric = _useState22[0],
    setUMAPMetric = _useState22[1];
  //const [umapSpread, setUMAPSpread] = useState(??);
  var _useState23 = useState(1),
    _useState24 = _slicedToArray(_useState23, 2),
    umapLocalConnectivity = _useState24[0],
    setUMAPLocalConnectivity = _useState24[1];
  var _useState25 = useState(5),
    _useState26 = _slicedToArray(_useState25, 2),
    umapNegativeSamplingRate = _useState26[0],
    setUMAPNegativeSamplingRate = _useState26[1];
  var _useState27 = useState(""),
    _useState28 = _slicedToArray(_useState27, 2),
    umapSuffix = _useState28[0],
    setUMAPSuffix = _useState28[1];

  ///////

  function enableUmap(value) {
    log.debug({
      value: value
    }, 'enableUmap');
    setUMAPEnabled(value);
    if (!value && layout === 'umap') {
      setLayout('fa2');
    }
    if (value && layout !== 'umap') {
      setLayout('umap');
    }
    var isAlreadyEnabled = visualizationBindings.remote && visualizationBindings.remote.layout && visualizationBindings.remote.layout.algorithm === 'umap';
    if (isAlreadyEnabled !== value) {
      log.debug({
        value: value
      }, 'effect umap enabled => viz');
      if (!value) {
        var remote = _objectSpread({}, visualizationBindings.remote || {});
        delete remote.layout;
        setVisualizationBindings(_objectSpread(_objectSpread({}, visualizationBindings), {}, {
          remote: remote
        }));
      } else {
        setVisualizationBindings(_objectSpread(_objectSpread({}, visualizationBindings), {}, {
          remote: _objectSpread(_objectSpread({}, visualizationBindings.remote || {}), {}, {
            layout: {
              layout: 'umap',
              library: 'pygraphistry',
              //kind, directed
              params: _objectSpread(_objectSpread(_objectSpread({}, umapColumns.length ? {
                X: umapColumns
              } : {}), umapTargets.length ? {
                y: umapTargets
              } : {}), {}, {
                encode_weight: umapEdges,
                preserveRegularEdges: !umapDropRegularEdges,
                play: umapPosition ? 0 : 5000,
                scale: umapScale,
                min_dist: umapMinDist,
                local_connectivity: umapLocalConnectivity,
                negative_sample_rate: umapNegativeSamplingRate,
                metric: umapMetric,
                suffix: umapSuffix
              })
            }
          })
        }));
      }
    } else {
      log.debug({
        isAlreadyEnabled: isAlreadyEnabled,
        value: value
      }, 'umap toggle unchanged, preserve viz');
    }
  }

  /////////////////////////////////////////////

  useEffect(function () {
    effectUmapArray(umapEnabled, visualizationBindings, setVisualizationBindings, umapColumns, 'X');
  }, [umapColumns]);
  useEffect(function () {
    effectUmapArray(umapEnabled, visualizationBindings, setVisualizationBindings, umapTargets, 'y');
  }, [umapTargets]);
  useEffect(function () {
    effectUmapAtomic(umapEnabled, visualizationBindings, setVisualizationBindings, umapEdges, 'encode_weight');
  }, [umapEdges]);
  useEffect(function () {
    effectUmapAtomic(umapEnabled, visualizationBindings, setVisualizationBindings, !umapDropRegularEdges, 'preserveRegularEdges');
  }, [umapDropRegularEdges]);
  useEffect(function () {
    effectUmapAtomic(umapEnabled, visualizationBindings, setVisualizationBindings, umapPosition ? 0 : 5000, 'play');
  }, [umapPosition]);
  useEffect(function () {
    effectUmapAtomic(umapEnabled, visualizationBindings, setVisualizationBindings, umapScale, 'scale');
  }, [umapScale]);
  useEffect(function () {
    effectUmapAtomic(umapEnabled, visualizationBindings, setVisualizationBindings, umapMinDist, 'min_dist');
  }, [umapMinDist]);
  useEffect(function () {
    effectUmapAtomic(umapEnabled, visualizationBindings, setVisualizationBindings, umapLocalConnectivity, 'local_connectivity');
  }, [umapLocalConnectivity]);
  useEffect(function () {
    effectUmapAtomic(umapEnabled, visualizationBindings, setVisualizationBindings, umapNegativeSamplingRate, 'negative_sample_rate');
  }, [umapNegativeSamplingRate]);
  useEffect(function () {
    effectUmapAtomic(umapEnabled, visualizationBindings, setVisualizationBindings, umapMetric, 'metric');
  }, [umapMetric]);
  useEffect(function () {
    effectUmapAtomic(umapEnabled, visualizationBindings, setVisualizationBindings, umapSuffix, 'suffix');
  }, [umapSuffix]);

  /////////////////////////////////////////////

  if (!visible) {
    return null;
  }
  log.debug({
    schema: schema,
    user: user,
    files: files,
    visualizationBindings: visualizationBindings,
    visualization: visualization
  }, 'Algorithms');
  var _bindingsReady = bindingsReady({
      files: files,
      visualizationBindings: visualizationBindings
    }),
    isReady = _bindingsReady.isReady,
    message = _bindingsReady.message;
  if (!isReady || !visualization.dataset_id) {
    return /*#__PURE__*/_jsx(Card, {
      className: "".concat(styles['sharer-fail-message']),
      children: /*#__PURE__*/_jsx(Card.Title, {
        children: !isReady ? 'Please finish configuring visualization first' : 'Visualization uploading...'
      })
    });
  }

  /////////////////////////////////////////////

  var umapOpts = {
    visualizationBindings: visualizationBindings,
    umapPosition: umapPosition,
    setUMAPPosition: setUMAPPosition,
    umapColumns: umapColumns,
    setUMAPColumns: setUMAPColumns,
    umapTargets: umapTargets,
    setUMAPTargets: setUMAPTargets,
    umapEdges: umapEdges,
    setUMAPEdges: setUMAPEdges,
    umapDropRegularEdges: umapDropRegularEdges,
    setUMAPDropRegularEdges: setUMAPDropRegularEdges,
    umapFeatureEngineering: umapFeatureEngineering,
    setUMAPFeatureEngineering: setUMAPFeatureEngineering,
    umapScale: umapScale,
    setUMAPScale: setUMAPScale,
    umapMinDist: umapMinDist,
    setUMAPMinDist: setUMAPMinDist,
    umapLocalConnectivity: umapLocalConnectivity,
    setUMAPLocalConnectivity: setUMAPLocalConnectivity,
    umapNegativeSamplingRate: umapNegativeSamplingRate,
    setUMAPNegativeSamplingRate: setUMAPNegativeSamplingRate,
    umapSuffix: umapSuffix,
    setUMAPSuffix: setUMAPSuffix,
    umapMetric: umapMetric,
    setUMAPMetric: setUMAPMetric,
    layout: layout,
    setLayout: setLayout
  };
  return wrapper(/*#__PURE__*/_jsxs("div", {
    className: styles['cleaner'],
    children: [/*#__PURE__*/_jsxs(Card, {
      className: "".concat(styles['viz-form-card'], " ").concat(styles["shaper"], " ").concat(styles["shaper-".concat(true ? 'enabled' : 'disabled')]),
      children: [/*#__PURE__*/_jsx(Card.Header, {
        className: styles['shaper-header'],
        children: "Attach the results of graph algorithms to nodes and edges"
      }), /*#__PURE__*/_jsxs(Card.Body, {
        children: [/*#__PURE__*/_jsx("p", {
          children: "Tip: Popular algorithms include betweeneness centrality, pagerank, k-core, and community (infomap, leidan, louvain)"
        }), /*#__PURE__*/_jsxs("p", {
          children: ["For more information on individual algorithms, see \xA0 ", /*#__PURE__*/_jsx("a", {
            href: "https://igraph.org/c/doc/igraph-Community.html",
            target: "_blank",
            children: "igraph"
          }), " (CPU) and \xA0 ", /*#__PURE__*/_jsx("a", {
            href: "https://docs.rapids.ai/api/cugraph/stable/",
            target: "_blank",
            children: "cugraph"
          }), " (GPU) documentation"]
        }), /*#__PURE__*/_jsx(CPUAlgs, {
          compute: visualizationBindings.remote.compute,
          options: schema.compute.igraph,
          setVisualizationBindings: setVisualizationBindings
        }), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(GPUAlgs, {
          compute: visualizationBindings.remote.compute,
          options: schema.compute.cugraph,
          setVisualizationBindings: setVisualizationBindings
        })]
      })]
    }), /*#__PURE__*/_jsxs(Card, {
      className: "".concat(styles['viz-form-card'], " ").concat(styles["shaper"], " ").concat(styles["shaper-".concat(true ? 'enabled' : 'disabled')]),
      children: [/*#__PURE__*/_jsxs(Card.Header, {
        className: styles['shaper-header'],
        children: [/*#__PURE__*/_jsx(RcSwitch, {
          onChange: function onChange(value) {
            enableUmap(value);
          },
          checked: umapEnabled,
          checkedChildren: 'on',
          unCheckedChildren: 'on'
        }), "UMAP: Link nodes with similar attributes"]
      }), /*#__PURE__*/_jsxs(Card.Body, {
        children: ["Cluster similar nodes using ", /*#__PURE__*/_jsx("a", {
          href: "https://umap-learn.readthedocs.io/en/latest/how_umap_works.html",
          target: "_blank",
          children: "UMAP"
        }), ":", /*#__PURE__*/_jsxs("ul", {
          children: [/*#__PURE__*/_jsx("li", {
            children: "Position nodes: Place nodes with similar attribute values near one another"
          }), /*#__PURE__*/_jsx("li", {
            children: "Link nodes: Create edges between nodes with similar attribute values"
          }), /*#__PURE__*/_jsxs("li", {
            children: ["See documentation for using ", /*#__PURE__*/_jsx("a", {
              href: "https://umap-learn.readthedocs.io/en/latest/api.html",
              target: "_blank",
              children: "individual parameters"
            }), ".", /*#__PURE__*/_jsx("i", {
              children: "Note:"
            }), " Graphistry's implementation factors in edge information when available. "]
          })]
        }), umapEnabled ? /*#__PURE__*/_jsx(UMAPOptions, _objectSpread({}, umapOpts)) : null]
      })]
    })]
  }));
}